// components/AlbumPasswordModal.js
// Modal for verifying album password before accessing protected content
import React, { useState, useRef, useEffect } from 'react';
import { FiLock, FiKey, FiEye, FiEyeOff, FiUnlock, FiAlertCircle, FiImage } from 'react-icons/fi';
import { verifyAlbumPassword } from '../services/albumService';

const AlbumPasswordModal = ({ album, onVerified, onClose }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 300);
  }, []);

  const handleVerify = async () => {
    if (!password.trim()) {
      setError('Please enter the password');
      return;
    }

    setVerifying(true);
    setError(null);

    try {
      const result = await verifyAlbumPassword(album.id, password);

      if (result.success && result.verified) {
        onVerified();
      } else {
        setError('Incorrect password. Please try again.');
        setPassword('');
      }
    } catch (err) {
      setError('Failed to verify password. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleVerify();
    }
  };

  const getCreatorName = () => {
    if (album?.users?.display_name) return album.users.display_name;
    if (album?.users?.first_name) {
      return `${album.users.first_name} ${album.users.last_name || ''}`.trim();
    }
    return 'Creator';
  };

  const getStudioName = () => album?.studios?.name || null;
  const coverImage = album?.cover_photo_url || album?.cover_photo_path || null;

  return (
    <div className="album-password-overlay">
      <div className="album-password-modal">
        {/* Lock icon */}
        <div className="lock-icon">
          <FiLock />
        </div>

        {/* Album preview */}
        <div className="album-preview">
          {coverImage ? (
            <img src={coverImage} alt={album?.name} className="album-cover" />
          ) : (
            <div className="album-cover-placeholder">
              <FiImage />
            </div>
          )}
          <div className="album-info">
            <div className="album-name">{album?.name || 'Album'}</div>
            {getStudioName() ? (
              <div className="album-creator studio">by {getStudioName()}</div>
            ) : (
              <div className="album-creator">by {getCreatorName()}</div>
            )}
          </div>
        </div>

        {/* Title */}
        <h2 className="password-title">This album is password protected</h2>
        <p className="password-subtitle">
          Enter the password shared by the album creator to view the photos
        </p>

        {/* Password input */}
        <div className="password-input-container">
          <FiKey className="input-icon" />
          <input
            ref={inputRef}
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={verifying}
            autoComplete="off"
          />
          <button
            className="toggle-password"
            onClick={() => setShowPassword(!showPassword)}
            type="button"
          >
            {showPassword ? <FiEyeOff /> : <FiEye />}
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="password-error">
            <FiAlertCircle />
            <span>{error}</span>
          </div>
        )}

        {/* Verify button */}
        <button
          className="unlock-btn"
          onClick={handleVerify}
          disabled={verifying}
        >
          {verifying ? (
            <div className="spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
          ) : (
            <>
              <FiUnlock />
              <span>Unlock Album</span>
            </>
          )}
        </button>

        {/* Hint */}
        <p className="password-hint">
          Don't have the password? Contact the photographer or studio who shared this album with you.
        </p>
      </div>
    </div>
  );
};

export default AlbumPasswordModal;
